"use client";

import { useGlobal } from "@/app/providers/use-global";
import { Button } from "@/components/ui/button";
import GroqLink from "@/components/ui/groq-link";
import Icon from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { cn, getDomainFromWindow } from "@/lib/utils";
import { reportClick } from "@/lib/utils/analytics";
import { ls } from "@/lib/utils/ls";
import {
  useBlockedDomains,
  useDisposableDomains,
} from "@/shared/auth/disposable-domains";
import { useStytch } from "@stytch/nextjs";
import { type HTMLAttributes, type SyntheticEvent, useState } from "react";
import { toast } from "sonner";
import { isMobileDevice } from "../../lib/utils";

interface UserAuthFormProps extends HTMLAttributes<HTMLDivElement> {
  onSentEmail: (email: string) => void;
}

export default function UserAuthForm({
  className,
  onSentEmail,
  ...props
}: UserAuthFormProps) {
  const loginUrl = `${getDomainFromWindow()}/authenticate/login`;
  const signupUrl = `${getDomainFromWindow()}/authenticate/signup`;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [email, setEmail] = useState("");
  const stytch = useStytch();
  const { setIsLoginDialogOpen, setShowLoginCardInHome, isIframe } =
    useGlobal();

  const disposableDomains = useDisposableDomains();
  const blockedDomains = useBlockedDomains();

  async function onSubmit(event: SyntheticEvent) {
    event.preventDefault();
    setIsLoading(true);
    const emailDomain = email.substring(email.lastIndexOf("@") + 1) || "";
    if (!emailDomain) {
      toast.error("Invalid email", {
        description: "Please enter a valid email address",
      });
      setIsLoading(false);
      return;
    }
    if (
      disposableDomains.includes(emailDomain) ||
      blockedDomains.includes(emailDomain)
    ) {
      toast.error("Invalid email", {
        description: "The email you provided is not supported.",
      });
      setIsLoading(false);
      return;
    }

    try {
      await stytch.magicLinks.email.loginOrCreate(email, {
        signup_magic_link_url: signupUrl,
        signup_expiration_minutes: 60,
        login_magic_link_url: loginUrl,
        login_expiration_minutes: 60,
      });
      onSentEmail(email);
      window.localStorage.setItem("returnTo", document.location.toString());
    } catch (_error) {
      toast.error("Error sending email");
    } finally {
      setIsLoading(false);
    }
  }

  async function onGoogleClicked() {
    reportClick("google_login_clicked");
    setIsLoading(true);
    try {
      if (isIframe) {
        window.open(
          "/authenticate/login/auto-login-google-initialize",
          "_blank",
          "width=600,height=600,popup=true",
        );
      } else {
        await stytch.oauth.google.start({
          login_redirect_url: loginUrl,
          signup_redirect_url: signupUrl,
        });
        window.localStorage.setItem("returnTo", document.location.toString());
      }

      setIsLoginDialogOpen(false);
      setShowLoginCardInHome(false);
      ls.set("noTypeAnimation", "true");
    } catch (_error) {
      toast.error("Google login failed");
      setIsLoading(false);
    }
  }

  return (
    <div className={cn("grid gap-6 max-w-[420px]", className)} {...props}>
      <div className="grid gap-6 px-4">
        <form onSubmit={onSubmit}>
          <div className="grid gap-2">
            <div className="grid gap-1">
              <label
                className="sr-only"
                htmlFor="email"
                tabIndex={isMobileDevice() ? 0 : -1}
              >
                Email
              </label>
              <Input
                id="email"
                placeholder="name@example.com"
                type="email"
                value={email}
                autoCapitalize="none"
                autoComplete="email"
                autoCorrect="off"
                disabled={isLoading}
                onChange={(event) => setEmail(event.currentTarget.value)}
                className="rounded-none border-input"
              />
            </div>
            <Button
              className="rounded-none"
              disabled={isLoading}
              variant={"outline"}
            >
              Login with Email
            </Button>
          </div>
        </form>

        <div className="flex items-center">
          <div className="border-t border-input grow" />
          <span className="px-4 text-xs uppercase">Or continue with</span>
          <div className="border-t border-input grow" />
        </div>

        <Button
          className="rounded-none"
          disabled={isLoading}
          onClick={onGoogleClicked}
          type="button"
          variant="outline"
        >
          <Icon icon="google" className="mr-2" />
          Login with Google
        </Button>

        <div className="flex flex-col items-center">
          <p className="flex mt-3">
            Looking for an API?
            <GroqLink
              className="ml-1"
              href={{ pathname: process.env.NEXT_PUBLIC_GROQ_CLOUD }}
              isExternal
              onClick={() => reportClick("try_google_cloud_login")}
            >
              Try GroqCloud
            </GroqLink>
          </p>
        </div>
      </div>

      <p className="text-xs py-3 text-center">
        By continuing, I accept the
        <GroqLink
          className="mx-1"
          href="https://console.groq.com/docs/terms-of-sale"
          isExternal
          variant={"secondary"}
        >
          Terms of Sale
        </GroqLink>
        and acknowledge that I have read the .
        <GroqLink
          className="mx-1"
          href="https://groq.com/wp-content/uploads/2024/05/Groq-Privacy-Policy_Final_30MAY2024.pdf"
          isExternal
          variant={"secondary"}
        >
          Privacy Policy
        </GroqLink>
      </p>
    </div>
  );
}
